import { DsmButton, DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { FC, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import {
  CattleFeed,
  FreshGrass,
} from "../../../../models/Intervention/CattleIntervention";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import { useFieldArray, useFormContext } from "react-hook-form";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";

import BeefFeedFreshGrassComponent from "../../Baseline/Beef/BeefFeedFreshGrassComponent";
import { FormType } from "../../common";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import {
  CattleFeed as CattleFeedBaseline,
  TargetAnimal,
  FreshGrass as BaselineFreshGrass,
} from "../../../../models/Baseline/CattleBaseline";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import { Spacer } from "../../../helpers/Spacer";

type FreshGrassInput = {
  id: string;
  type: string;
  amount: string;
  crudeProteinContent: string;
  dryMatterContent: string;
  digestibility: string;
  neutralDetergentFiber: string;
  grossEnergyContent: string;
  ashContent: string;
};

interface BeefFeedFreshGrassProp {
  formType: FormType;
  fieldItemPrefix: string;
  baselineStageData: CattleFeedBaseline;
  currentFormValues: CattleFeed | undefined | null;
  target: TargetAnimal | undefined;
  isFreshGrassEdit: boolean;
  setIsFreshGrassEdit: (val: boolean) => void;
}

const BeefFeedFreshGrass: FC<BeefFeedFreshGrassProp> = ({
  formType,
  fieldItemPrefix,
  baselineStageData,
  currentFormValues,
  target,
  isFreshGrassEdit,
  setIsFreshGrassEdit,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const fc = useFormContext<BeefIntervention>();
  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const barnOutputMassUnit = unitLong(userUOM.unitBarnOutputMass as string);
  const isTargetAnimalCalves = target === TargetAnimal.CALVES;
  const [, forceUpdate] = useState({});

  let adornment: string;
  switch (barnOutputMassUnit) {
    case "kg":
      adornment = "kg DM";
      break;
    case "lbs":
      adornment = "lbs DM";
      break;
    default:
      barnOutputMassUnit || "kg DM";
      break;
  }

  const {
    fields: freshGrass,
    append: appendFreshGrass,
    remove: removeFreshGrass,
  } = useFieldArray({
    name: `${fieldItemPrefix}.freshGrassAdditions`,
    control: fc.control,
    keyName: "keyId",
  });

  const addFreshGrass = () => {
    const newFreshGrass: FreshGrassInput = {
      id: uuidv4(),
      type: "",
      amount: "",
      crudeProteinContent: "",
      dryMatterContent: "",
      digestibility: "",
      neutralDetergentFiber: "",
      grossEnergyContent: "",
      ashContent: "",
    };
    appendFreshGrass(newFreshGrass);
  };

  const cancelEditionHandler = () => {
    (baselineStageData?.freshGrass || [])
      ?.filter((item) => !!item.type)
      ?.forEach((_, index) => {
        const lastValue = currentFormValues?.freshGrass?.[index];
        fc.setValue(
          `${fieldItemPrefix}.freshGrass[${index}].amount`,
          lastValue?.amount ?? ""
        );
        fc.setValue(
          `${fieldItemPrefix}.freshGrass[${index}].crudeProteinContent`,
          lastValue?.crudeProteinContent ?? ""
        );
        fc.setValue(
          `${fieldItemPrefix}.freshGrass[${index}].dryMatterContent`,
          lastValue?.dryMatterContent ?? ""
        );
        fc.setValue(
          `${fieldItemPrefix}.freshGrass[${index}].digestibility`,
          lastValue?.digestibility ?? ""
        );
        fc.setValue(
          `${fieldItemPrefix}.freshGrass[${index}].neutralDetergentFiber`,
          lastValue?.neutralDetergentFiber ?? ""
        );
        fc.setValue(
          `${fieldItemPrefix}.freshGrass[${index}].grossEnergyContent`,
          lastValue?.grossEnergyContent ?? ""
        );
        fc.setValue(
          `${fieldItemPrefix}.freshGrass[${index}].ashContent`,
          lastValue?.ashContent ?? ""
        );
      });

    for (let i = freshGrass.length; i >= 0; i--) {
      removeFreshGrass(i);
    }

    const lastAdditions =
      (currentFormValues?.freshGrassAdditions as BaselineFreshGrass[]) ?? [];

    lastAdditions.forEach((addition) => {
      appendFreshGrass(addition);
    });

    setIsFreshGrassEdit(false);
    forceUpdate({});
  };

  const getCorrectBorderDisplay = () => {
    if ((baselineStageData.freshGrass?.length ?? 0) > 1 || freshGrass.length)
      return classes.dsmGridTwoColumnNoRowSpacingWithBorder;

    return classes.dsmGridTwoColumnNoRowSpacing;
  };

  const freshGrassUnit = (fg: FreshGrass, index: number) => {
    const fieldPrefix = `${fieldItemPrefix}.freshGrass[${index}]`;

    return (
      <DsmGrid className={getCorrectBorderDisplay()}>
        <Spacer />
        <Spacer />
        {!isFreshGrassEdit ? (
          <>
            <ReactHookDsmInput
              name={`${fieldPrefix}.type`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.BEEF.FEED.FRESH_GRASS_NAME",
              })}
              tooltip={intl.formatMessage({
                id: isTargetAnimalCalves
                  ? "SUSTELL.STAGE.BEEF.FEED.FRESH_GRASS_NAME.CALVES.TOOLTIP"
                  : "SUSTELL.STAGE.BEEF.FEED.FRESH_GRASS_NAME.TOOLTIP",
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.BEEF.FEED.FRESH_GRASS_NAME.ADORNMENT",
              })}
              type="text"
              defaultValue={fg.type ?? ""}
              disabled
            />
            {isFieldFilled(fc.getValues(`${fieldPrefix}.amount`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.amount`}
                  label={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.AMOUNT",
                  })}
                  tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.AMOUNT.TOOLTIP",
                  })}
                  type="number"
                  metricUnit={adornment}
                  baseline={fg.amount}
                  displayTypeSelect={false}
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalAmount`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.AMOUNT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.AMOUNT.TOOLTIP",
                })}
                adornment={adornment}
                type="number"
                defaultValue={fg.amount ?? ""}
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.crudeProteinContent`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.crudeProteinContent`}
                  label={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.CRUDE_PROTEIN_CONTENT",
                  })}
                  tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.CRUDE_PROTEIN_CONTENT.TOOLTIP",
                  })}
                  type="number"
                  metricUnit={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.CRUDE_PROTEIN_CONTENT.ADORNMENT",
                  })}
                  baseline={fg.crudeProteinContent}
                  displayTypeSelect={false}
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalCrudeProteinContent`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.CRUDE_PROTEIN_CONTENT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.CRUDE_PROTEIN_CONTENT.TOOLTIP",
                })}
                adornment={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.CRUDE_PROTEIN_CONTENT.ADORNMENT",
                })}
                placeholder={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.FRESH_GRASS.CRUDE_PROTEIN_CONTENT.PLACEHOLDER",
                })}
                type="number"
                defaultValue={fg.crudeProteinContent ?? ""}
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.dryMatterContent`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.dryMatterContent`}
                  label={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.DRY_MATTER_CONTENT",
                  })}
                  tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.DRY_MATTER_CONTENT.TOOLTIP",
                  })}
                  type="number"
                  metricUnit={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.DRY_MATTER_CONTENT.ADORNMENT",
                  })}
                  baseline={fg.dryMatterContent}
                  displayTypeSelect={false}
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalDryMatterContent`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DRY_MATTER_CONTENT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DRY_MATTER_CONTENT.TOOLTIP",
                })}
                adornment={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DRY_MATTER_CONTENT.ADORNMENT",
                })}
                placeholder={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.FRESH_GRASS.DRY_MATTER_CONTENT.PLACEHOLDER",
                })}
                type="number"
                defaultValue={fg.dryMatterContent ?? ""}
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.digestibility`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.digestibility`}
                  label={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.DIGESTIBILITY",
                  })}
                  tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.DIGESTIBILITY.TOOLTIP",
                  })}
                  type="number"
                  metricUnit={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.DIGESTIBILITY.ADORNMENT",
                  })}
                  baseline={fg.digestibility}
                  displayTypeSelect={false}
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalDigestibility`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DIGESTIBILITY",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DIGESTIBILITY.TOOLTIP",
                })}
                adornment={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DIGESTIBILITY.ADORNMENT",
                })}
                placeholder={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.FRESH_GRASS.DIGESTIBILITY.PLACEHOLDER",
                })}
                type="number"
                defaultValue={fg.digestibility ?? ""}
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.grossEnergyContent`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.grossEnergyContent`}
                  label={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.GROSS_ENERGY_CONTENT",
                  })}
                  tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.GROSS_ENERGY_CONTENT.TOOLTIP",
                  })}
                  type="number"
                  metricUnit={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.GROSS_ENERGY_CONTENT.ADORNMENT",
                  })}
                  baseline={fg.grossEnergyContent}
                  displayTypeSelect={false}
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalGrossEnergyContent`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.GROSS_ENERGY_CONTENT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.GROSS_ENERGY_CONTENT.TOOLTIP",
                })}
                adornment={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.GROSS_ENERGY_CONTENT.ADORNMENT",
                })}
                placeholder={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.FRESH_GRASS.GROSS_ENERGY_CONTENT.PLACEHOLDER",
                })}
                type="number"
                defaultValue={fg.grossEnergyContent ?? ""}
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.ashContent`)) ? (
              <div>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.ashContent`}
                  label={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.ASH_CONTENT",
                  })}
                  tooltip={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.ASH_CONTENT.TOOLTIP",
                  })}
                  type="number"
                  metricUnit={intl.formatMessage({
                    id: "SUSTELL.STAGE.BEEF.FEED.ASH_CONTENT.ADORNMENT",
                  })}
                  baseline={fg.ashContent}
                  displayTypeSelect={false}
                  disabled
                />
                <Spacer />
              </div>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalAshContent`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.ASH_CONTENT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.ASH_CONTENT.TOOLTIP",
                })}
                adornment={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.ASH_CONTENT.ADORNMENT",
                })}
                placeholder={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.FRESH_GRASS.ASH_CONTENT.PLACEHOLDER",
                })}
                type="number"
                defaultValue={fg.ashContent ?? ""}
                disabled
              />
            )}
          </>
        ) : (
          <>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.amount`}
                label={fg.type as string}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.AMOUNT.TOOLTIP",
                })}
                type="number"
                metricUnit={adornment}
                baseline={fg.amount}
                disabled={false}
              />
              <Spacer />
            </div>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.crudeProteinContent`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.CRUDE_PROTEIN_CONTENT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.CRUDE_PROTEIN_CONTENT.TOOLTIP",
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.CRUDE_PROTEIN_CONTENT.ADORNMENT",
                })}
                baseline={fg.crudeProteinContent}
                disabled={false}
              />
              <Spacer />
            </div>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.dryMatterContent`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DRY_MATTER_CONTENT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DRY_MATTER_CONTENT.TOOLTIP",
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DRY_MATTER_CONTENT.ADORNMENT",
                })}
                baseline={fg.dryMatterContent}
                disabled={false}
              />
              <Spacer />
            </div>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.digestibility`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DIGESTIBILITY",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DIGESTIBILITY.TOOLTIP",
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.DIGESTIBILITY.ADORNMENT",
                })}
                baseline={fg.digestibility}
                disabled={false}
              />
              <Spacer />
            </div>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.grossEnergyContent`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.GROSS_ENERGY_CONTENT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.GROSS_ENERGY_CONTENT.TOOLTIP",
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.GROSS_ENERGY_CONTENT.ADORNMENT",
                })}
                baseline={fg.grossEnergyContent}
                disabled={false}
              />
              <Spacer />
            </div>
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.ashContent`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.ASH_CONTENT",
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.ASH_CONTENT.TOOLTIP",
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: "SUSTELL.STAGE.BEEF.FEED.ASH_CONTENT.ADORNMENT",
                })}
                baseline={fg.ashContent}
                disabled={false}
              />
              <Spacer />
            </div>
          </>
        )}
      </DsmGrid>
    );
  };

  return (
    <>
      <p className={classes.headingTitleVariant}>
        {intl.formatMessage({ id: "SUSTELL.STAGE.FEED.FRESH_GRASS" })}
      </p>

      <DsmGrid className={classes.dsmGridOneColumnInterventionWithBorder}>
        {(baselineStageData?.freshGrass || [])
          ?.filter((item) => !!item.type)
          ?.map((freshGrassItem, index: number) =>
            freshGrassUnit(freshGrassItem, index)
          )}
        {!!freshGrass.length &&
          freshGrass.map((item, index) => (
            <BeefFeedFreshGrassComponent
              key={item.keyId}
              fieldItemPrefix={`${fieldItemPrefix}.freshGrassAdditions`}
              formType={formType}
              itemIndex={index}
              removeHandler={(index) => removeFreshGrass(index)}
              isOnEditionMode={isFreshGrassEdit}
              isIntervention
              isTargetAnimalCalves={isTargetAnimalCalves}
            />
          ))}
        {!isFreshGrassEdit ? (
          <>
            {formType !== FormType.View &&
              <DsmButton
                variant="text"
                onClick={() => setIsFreshGrassEdit(true)}
                style={{ width: "120px" }}
              >
                <DsmIcon slot="before" name="general/edit-02" />
                <u>
                  {intl.formatMessage({
                    id: "INTERVENTION.FORM.EDIT_INPUT",
                  })}
                </u>
              </DsmButton>}
          </>
        ) : (
          <>
            <DsmButton
              variant="text"
              onClick={addFreshGrass}
              disabled={formType === FormType.View}
            >
              <DsmIcon slot="before" name="general/plus-circle" />
              {intl.formatMessage({ id: "GENERAL.ADD.ANOTHER" })}{" "}
              {intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.FEED.GRASS" })}
            </DsmButton>
            <DsmButton
              variant="text"
              destructive
              onClick={() => cancelEditionHandler()}
              disabled={formType === FormType.View}
              style={{ width: "160px" }}
            >
              <DsmIcon slot="before" name="general/minus-circle" />
              <u>
                {intl.formatMessage({
                  id: "GENERAL.CANCEL",
                })}
              </u>
            </DsmButton>
          </>
        )}
      </DsmGrid>
    </>
  );
};

export default BeefFeedFreshGrass;
